import {
  AddCircleOutline,
  ArrowDropDown,
  CameraAlt,
  Close,
  PhotoLibrary,
  RemoveCircleOutline
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";
import { Radio, Select, TextField } from "components/UI";
import { DateField } from "components/UI/InputFields/DateField";
import { ADDRESSES } from "features/members";
import { useEffect, useRef, useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCities, useCountries, useRegions, useZones } from "../../api";
import { useMembersStore } from "../../stores";

export const PersonalProfileSection = ({
  register,
  errors,
  watch,
  isEditMode,
  setValue,
  control
}) => {
  const profileImage = useMembersStore(state => state.memberData.profile_image);
  const setProfileImage = useMembersStore(state => state.setProfileImage);
  const idImage = useMembersStore(state => state.memberData.id_image);
  const setIdImage = useMembersStore(state => state.setIdImage);
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const { t } = useTranslation();

  const hiddenFileInput = useRef(null);
  const hiddenIDFileInput = useRef(null);
  const hiddenCameraInput = useRef(null);
  const hiddenIDCameraInput = useRef(null);

  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [idMenuAnchor, setIdMenuAnchor] = useState(null);

  // Detect if device is mobile
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  const handleProfileMenuOpen = event => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleIdMenuOpen = event => {
    setIdMenuAnchor(event.currentTarget);
  };

  const handleIdMenuClose = () => {
    setIdMenuAnchor(null);
  };

  const handleGalleryClick = () => {
    hiddenFileInput.current.click();
    handleProfileMenuClose();
  };

  const handleCameraClick = () => {
    hiddenCameraInput.current.click();
    handleProfileMenuClose();
  };

  const handleIdGalleryClick = () => {
    hiddenIDFileInput.current.click();
    handleIdMenuClose();
  };

  const handleIdCameraClick = () => {
    hiddenIDCameraInput.current.click();
    handleIdMenuClose();
  };

  const handleChange = event => {
    const fileUploaded = event.target.files[0];
    if (fileUploaded) {
      setProfileImage(fileUploaded);
    }
  };

  const handleIdChange = event => {
    const fileUploaded = event.target.files[0];
    if (fileUploaded) {
      setIdImage(fileUploaded);
    }
  };

  const handleRemoveProfileImage = () => {
    setProfileImage(null);
  };

  const handleRemoveIdImage = () => {
    setIdImage(null);
  };

  const countriesQuery = useCountries();
  const regionsQuery = useRegions({
    countryId: watch("country_id"),
    config: {
      enabled: !!watch("country_id")
    }
  });
  const zonesQuery = useZones({
    regionId: watch("region_id"),
    config: {
      enabled: !!watch("region_id")
    }
  });
  const citiesQuery = useCities({
    zoneId: watch("zone_id"),
    config: {
      enabled: !!watch("zone_id")
    }
  });

  useEffect(() => {
    if (countriesQuery.data) {
      if (isEditMode && fullMemberData) {
        setValue(
          "nationality_id",
          String(fullMemberData?.nationality_id || "")
        );
        setValue("country_id", String(fullMemberData?.country_id || ""));
      } else {
        // set default nationality and country to Ethiopia
        setValue("nationality_id", "66");
        setValue("country_id", "66");
      }
    }
  }, [countriesQuery.data, isEditMode, fullMemberData]);

  useEffect(() => {
    if (regionsQuery.data && isEditMode && fullMemberData) {
      setValue("region_id", String(fullMemberData?.region_id || ""));
    }
  }, [regionsQuery.data, isEditMode, fullMemberData]);

  useEffect(() => {
    if (zonesQuery.data && isEditMode && fullMemberData) {
      setValue("zone_id", String(fullMemberData?.zone_id || ""));
    }
  }, [zonesQuery.data, isEditMode, fullMemberData]);

  useEffect(() => {
    if (citiesQuery.data && isEditMode && fullMemberData) {
      setValue("city_id", String(fullMemberData?.city_id || ""));
    }
  }, [citiesQuery.data, isEditMode, fullMemberData]);

  const newLanguageField = {
    language: ""
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "languages"
  });

  const isOutsideEthiopia = String(watch("country_id")) !== "66";

  return (
    <Grid container columnSpacing={{ xs: 2, md: 12 }}>
      <Grid item xs={12} md={3}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="body1">
            {t("addMembers.step1.PersonalProfile")}
          </Typography>
          <Box sx={{ position: "relative" }}>
            <Avatar
              variant="rounded"
              src={profileImage ? URL.createObjectURL(profileImage) : ""}
              alt=""
              sx={{
                width: {
                  xs: 80,
                  md: 150,
                  xl: 250
                },
                height: {
                  xs: 80,
                  md: 150,
                  xl: 250
                }
              }}
            />
            {profileImage && (
              <IconButton
                onClick={handleRemoveProfileImage}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  backgroundColor: "error.main",
                  color: "white",
                  width: 28,
                  height: 28,
                  "&:hover": {
                    backgroundColor: "error.dark"
                  }
                }}
                size="small"
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Stack alignItems="center">
            <input
              accept="image/*"
              type="file"
              ref={hiddenFileInput}
              onChange={handleChange}
              hidden
            />
            <input
              accept="image/*"
              type="file"
              capture="user"
              ref={hiddenCameraInput}
              onChange={handleChange}
              hidden
            />
            <Typography variant="subtitle2">
              {t("addMembers.step1.UploadPhoto")}
            </Typography>
            <Button
              size="large"
              variant="contained"
              onClick={handleProfileMenuOpen}
              endIcon={<ArrowDropDown />}
            >
              {t("ui.dropzone.browse")}
            </Button>
            <Menu
              anchorEl={profileMenuAnchor}
              open={Boolean(profileMenuAnchor)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem onClick={handleGalleryClick}>
                <ListItemIcon>
                  <PhotoLibrary fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {t("addMembers.step1.ChooseFromGallery")}
                </ListItemText>
              </MenuItem>
              {isMobile && (
                <MenuItem onClick={handleCameraClick}>
                  <ListItemIcon>
                    <CameraAlt fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t("addMembers.step1.TakePhoto")}</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Stack>
        </Stack>
      </Grid>
      <Grid container item xs={12} md={9} spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t("addMembers.step1.Title")}
            name="title"
            error={!!errors.title?.message}
            helperText={errors.title?.message}
            register={register}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t("form.common.firstName")}
            name="first_name"
            error={!!errors.first_name?.message}
            helperText={errors.first_name?.message}
            register={register}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t("form.common.fatherName")}
            name="father_name"
            error={!!errors.father_name?.message}
            helperText={errors.father_name?.message}
            register={register}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t("form.common.grandFatherName")}
            name="last_name"
            error={!!errors.last_name?.message}
            helperText={errors.last_name?.message}
            register={register}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Radio
            label={t("form.common.gender")}
            name="gender"
            buttons={[
              {
                radioLabel: t("form.common.male"),
                value: "male",
                id: "gender_male"
              },
              {
                radioLabel: t("form.common.female"),
                value: "female",
                id: "gender_female"
              }
            ]}
            row={true}
            register={register}
            error={!!errors.gender?.message}
            helperText={errors.gender?.message}
            required
          />
        </Grid>
        <Grid item xs={12} sm={12} md={8}>
          <Select
            isLabelBold={0}
            label={t("addMembers.step1.Nationality")}
            name="nationality_id"
            options={
              countriesQuery.data?.result?.map(country => ({
                optionLabel: country?.nationality,
                value: country?.id
              })) || []
            }
            register={register}
            error={!!errors.nationality_id?.message}
            helperText={errors.nationality_id?.message}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={t("addMembers.step1.MotherFirstName")}
            name="mother_first_name"
            error={!!errors.mother_first_name?.message}
            helperText={errors.mother_first_name?.message}
            register={register}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={t("addMembers.step1.MotherFatherName")}
            name="mother_father_name"
            error={!!errors.mother_father_name?.message}
            helperText={errors.mother_father_name?.message}
            register={register}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={t("addMembers.step1.MotherGrandfatherName")}
            name="mother_grand_father_name"
            error={!!errors.mother_grand_father_name?.message}
            helperText={errors.mother_grand_father_name?.message}
            register={register}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Select
            isLabelBold={0}
            label={t("addMembers.step1.BirthCountry")}
            name="country_id"
            options={
              countriesQuery.data?.result?.map(country => ({
                optionLabel: country?.name,
                value: country?.id
              })) || []
            }
            register={register}
            error={!!errors.country_id?.message}
            helperText={errors.country_id?.message}
          />
        </Grid>
        {!isOutsideEthiopia && (
          <Grid item xs={12} sm={6} md={3}>
            <Select
              isLabelBold={0}
              label={t("addMembers.step1.BirthRegion")}
              name={`region_id`}
              options={
                regionsQuery.data?.result?.map(region => ({
                  optionLabel: region?.name,
                  value: region?.id
                })) || []
              }
              register={register}
              error={!!errors.region_id?.message}
              helperText={errors.region_id?.message}
              onChange={e => {
                setValue("region_id", String(e.target.value), {
                  shouldValidate: true
                });

                if (
                  String(e.target.value) ===
                  String(ADDRESSES.ADDIS_ABABA.REGION_ID)
                ) {
                  setValue("zone_id", String(ADDRESSES.ADDIS_ABABA.ZONE_ID), {
                    shouldValidate: true
                  });
                  setValue("city_id", String(ADDRESSES.ADDIS_ABABA.CITY_ID), {
                    shouldValidate: true
                  });
                } else {
                  setValue("zone_id", "", {
                    shouldValidate: true
                  });
                  setValue("city_id", "", {
                    shouldValidate: true
                  });
                }
              }}
            />
          </Grid>
        )}
        {isOutsideEthiopia && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Birth City"
                name="birth_city"
                error={!!errors.birth_city?.message}
                helperText={errors.birth_city?.message}
                register={register}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Birth State"
                name="birth_state"
                error={!!errors.birth_state?.message}
                helperText={errors.birth_state?.message}
                register={register}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Birth Zip Code"
                name="birth_zip_code"
                error={!!errors.birth_zip_code?.message}
                helperText={errors.birth_zip_code?.message}
                register={register}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Birth Street Address"
                name="birth_street_address"
                error={!!errors.birth_street_address?.message}
                helperText={errors.birth_street_address?.message}
                register={register}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Birth Building Number"
                name="birth_building_number"
                error={!!errors.birth_building_number?.message}
                helperText={errors.birth_building_number?.message}
                register={register}
              />
            </Grid>
          </>
        )}
        {!isOutsideEthiopia &&
          String(watch("region_id")) !==
            String(ADDRESSES.ADDIS_ABABA.REGION_ID) && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Select
                  isLabelBold={0}
                  label={t("addMembers.step1.BirthZone")}
                  name={`zone_id`}
                  options={
                    zonesQuery.data?.result?.map(zone => ({
                      optionLabel: zone?.name,
                      value: zone?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.zone_id?.message}
                  helperText={errors.zone_id?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Select
                  isLabelBold={0}
                  label={t("addMembers.step1.BirthCity")}
                  name={`city_id`}
                  options={
                    citiesQuery.data?.result?.map(city => ({
                      optionLabel: city?.name,
                      value: city?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.city_id?.message}
                  helperText={errors.city_id?.message}
                />
              </Grid>
            </>
          )}
        <Grid item xs={12} sm={6} md={6}>
          <Controller
            name="date_of_birth"
            control={control}
            render={({ field }) => (
              <DateField
                label={t("form.common.dateOfBirth")}
                name={field.name}
                error={!!errors.date_of_birth?.message}
                helperText={errors.date_of_birth?.message}
                {...field}
                required
                disableFutureDates
              />
            )}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: { xs: "none", md: "block" } }}
        />
        <Grid item xs={12} sm={6} md={4}>
          <Select
            label={t("addMembers.step1.IdType")}
            name="id_type"
            options={[
              {
                optionLabel: t("form.common.none"),
                value: ""
              },
              {
                optionLabel: t("addMembers.step1.GovernmentIssued"),
                value: "Government issued"
              },
              {
                optionLabel: t("addMembers.step1.License"),
                value: "license"
              },
              {
                optionLabel: t("addMembers.step1.Passport"),
                value: "passport"
              }
            ]}
            register={register}
            error={!!errors.id_type?.message}
            helperText={errors.id_type?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={t("addMembers.step1.IdNumber")}
            name="id_number"
            error={!!errors.id_number?.message}
            helperText={errors.id_number?.message}
            register={register}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <Stack alignItems="flex-start">
              <input
                accept="image/*"
                type="file"
                ref={hiddenIDFileInput}
                onChange={handleIdChange}
                hidden
              />
              <input
                accept="image/*"
                type="file"
                capture="environment"
                ref={hiddenIDCameraInput}
                onChange={handleIdChange}
                hidden
              />
              <Typography variant="subtitle2">
                {t("addMembers.step1.UploadYourId")}
              </Typography>
              <Button
                size="large"
                variant="contained"
                onClick={handleIdMenuOpen}
                endIcon={<ArrowDropDown />}
              >
                {t("ui.dropzone.browse")}
              </Button>
              <Menu
                anchorEl={idMenuAnchor}
                open={Boolean(idMenuAnchor)}
                onClose={handleIdMenuClose}
              >
                <MenuItem onClick={handleIdGalleryClick}>
                  <ListItemIcon>
                    <PhotoLibrary fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {t("addMembers.step1.ChooseFromGallery")}
                  </ListItemText>
                </MenuItem>
                {isMobile && (
                  <MenuItem onClick={handleIdCameraClick}>
                    <ListItemIcon>
                      <CameraAlt fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      {t("addMembers.step1.TakePhoto")}
                    </ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </Stack>
            <Box sx={{ position: "relative" }}>
              <Avatar
                variant="rounded"
                src={idImage ? URL.createObjectURL(idImage) : ""}
                alt="Image"
                sx={{ width: 100, height: 100 }}
              />
              {idImage && (
                <IconButton
                  onClick={handleRemoveIdImage}
                  sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    backgroundColor: "error.main",
                    color: "white",
                    width: 24,
                    height: 24,
                    "&:hover": {
                      backgroundColor: "error.dark"
                    }
                  }}
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          {fields.map((field, index) => (
            <Stack direction="row" alignItems="center" key={field.id} mb={2}>
              <TextField
                label={t("addMembers.step1.Language")}
                name={`languages.${index}.language`}
                error={!!errors.languages?.[index]?.language?.message}
                helperText={errors.languages?.[index]?.language?.message}
                register={register}
              />
              {index !== 0 && (
                <IconButton
                  sx={{
                    alignSelf: "flex-end"
                  }}
                  onClick={() => remove(index)}
                >
                  <RemoveCircleOutline color="primary" />
                </IconButton>
              )}
            </Stack>
          ))}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <IconButton onClick={() => append(newLanguageField)}>
            <AddCircleOutline color="white" />
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
